import { RecurrenceField, type RecurrenceValue } from "../components/RecurrenceField.js";
import { recurrenceText } from "@probetalab/contracts";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import type { CalendarDueItem, CalendarEntry, CalendarOccurrence, CalendarRecurrence, PatientSummary } from "@probetalab/contracts";
import { get, run } from "../lib/api.js";
import { type CalendarMode, byDay, calendarScope, daySummary, daysOf, dueChip, dueTarget, periodLabel, rangeFor, shiftAnchor, visibleDue, weekdayShort } from "../lib/calendar.js";
import { type BranchToneOf, branchToneLookup, toneStyle } from "../lib/branchColors.js";
import { fmtDueDay, plural, todayIso } from "../lib/format.js";
import { useBranch, useSession } from "../lib/session.js";
import { useToast } from "../lib/toast.js";
import { errorMessage, useQuery } from "../lib/useQuery.js";
import { Button, Chip, Empty, Field, IconButton, Input, Loading, PageHead, Segmented, Select, Sheet, Textarea } from "../components/ui.js";
import { PatientSearch } from "../components/PatientPicker.js";
import { Icon } from "../components/icons.js";
import { NO_AUTOFILL } from "../lib/noAutofill.js";

/**
 * Calendario (pilot request 2026-11-03, ADR 0027): "un calendario en el menú, en donde se coloquen
 * automáticamente la fecha de entrega … para tener un control digital y que no se nos pase". Two
 * things on one calendar: the studies promised for each day (from each study's delivery rule,
 * automatic) and the staff's own notes, like the paper planner at the counter ("Depositar",
 * "RPBI", "Internet Capulín", "Cita … muestra"). Day, week and month; today highlighted; overdue
 * studies always on top, whatever the period, so a week view never hides last week's slip.
 *
 * Same-day studies (a BH promised for its own registration day) are many and already tracked in
 * Entrega, so they are hidden unless «Incluir los del mismo día» is on; the setting is per device.
 */

const MODE_KEY = "probeta.calendar.mode";
const SAME_DAY_KEY = "probeta.calendar.same_day";
const ALL = "all";

function readMode(): CalendarMode {
  const v = typeof localStorage !== "undefined" ? localStorage.getItem(MODE_KEY) : null;
  return v === "day" || v === "month" ? v : "week";
}

export function CalendarPage() {
  const branch = useBranch();
  const { me, can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const today = todayIso();
  const anchor = /^\d{4}-\d{2}-\d{2}$/.test(params.get("dia") ?? "") ? params.get("dia")! : today;
  const [mode, setModeState] = useState<CalendarMode>(readMode);
  // No second branch selector (Fer, 2026-11-03): the page follows the app's branch, plus «Ver ambas».
  const [both, setBoth] = useState(false);
  const [sameDay, setSameDay] = useState(() => typeof localStorage !== "undefined" && localStorage.getItem(SAME_DAY_KEY) === "1");
  const [editing, setEditing] = useState<{ entry: CalendarEntry | null; date: string } | null>(null);
  const [showAllOverdue, setShowAllOverdue] = useState(false);
  const branches = me?.branches ?? [];
  const scope = calendarScope(both, branch, branches);
  const range = rangeFor(mode, anchor);
  const view = useQuery(
    () => get("calendar", { query: { from: range.from, to: range.to, ...(scope.branchId ? { branch_id: scope.branchId } : {}) } }),
    [range.from, range.to, scope.branchId],
  );
  const labToday = view.data?.today ?? today;

  const setMode = (m: CalendarMode) => {
    setModeState(m);
    localStorage.setItem(MODE_KEY, m);
  };
  const setAnchor = (d: string) => setParams(d === today ? {} : { dia: d }, { replace: true });
  const openDay = (d: string) => {
    setMode("day");
    setAnchor(d);
  };
  useEffect(() => localStorage.setItem(SAME_DAY_KEY, sameDay ? "1" : "0"), [sameDay]);

  const due = useMemo(() => visibleDue(view.data?.due ?? [], sameDay), [view.data, sameDay]);
  const overdue = useMemo(() => visibleDue(view.data?.overdue ?? [], sameDay), [view.data, sameDay]);
  const dueByDay = useMemo(() => byDay(due, (d) => d.due_date), [due]);
  const entriesByDay = useMemo(() => byDay(view.data?.entries ?? [], (e) => e.date), [view.data]);
  const branchName = (id: string | null) => (id === null ? "Todas las sucursales" : (branches.find((b) => b.id === id)?.name ?? ""));
  const canCapture = can("quimico");
  const open = (it: CalendarDueItem) => navigate(dueTarget(it, canCapture));

  async function toggleDone(o: CalendarOccurrence) {
    try {
      await run("calendar.entry.done", { entry_id: o.entry.id, date: o.date, done: !o.done }, { branch_id: branch.id });
      await view.reload();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  const toneOf = branchToneLookup(both, branches);
  const dayProps = { toneOf, labToday, dueByDay, entriesByDay, open, toggleDone, onEdit: (o: CalendarOccurrence) => setEditing({ entry: o.entry, date: o.date }), showBranch: scope.branchId === null, branchName };

  return (
    <>
      <PageHead
        kicker={<span data-testid="cal-kicker">{scope.label}</span>}
        title="Calendario"
        lede="Las fechas de entrega se colocan solas según cada estudio. Agrega también tus pendientes: depósitos, RPBI, pagos, citas."
        actions={
          <Button variant="primary" icon="plus" onClick={() => setEditing({ entry: null, date: mode === "day" ? anchor : labToday })}>
            Agregar nota
          </Button>
        }
      />

      <div className="cal-toolbar">
        <Segmented<CalendarMode>
          value={mode}
          onChange={setMode}
          options={[
            { value: "day", label: "Día" },
            { value: "week", label: "Semana" },
            { value: "month", label: "Mes" },
          ]}
        />
        <div className="cal-nav">
          <IconButton icon="chevronLeft" label="Anterior" onClick={() => setAnchor(shiftAnchor(mode, anchor, -1))} />
          <Button variant="secondary" onClick={() => setAnchor(today)} disabled={anchor === today}>
            Hoy
          </Button>
          <IconButton icon="chevronRight" label="Siguiente" onClick={() => setAnchor(shiftAnchor(mode, anchor, 1))} />
          <h2 className="cal-period" aria-live="polite">{periodLabel(mode, anchor)}</h2>
        </div>
        <div className="cal-filters">
          {branches.length > 1 && (
            <label className="cal-check">
              <input type="checkbox" checked={both} onChange={(e) => setBoth(e.target.checked)} />
              {scope.toggleLabel}
            </label>
          )}
          <label className="cal-check">
            <input type="checkbox" checked={sameDay} onChange={(e) => setSameDay(e.target.checked)} />
            Incluir los del mismo día
          </label>
        </div>
      </div>

      {both && branches.length > 1 && <BranchLegend branches={branches} toneOf={toneOf} />}

      {overdue.length > 0 && (
        <section className="cal-overdue" aria-label="Atrasados" data-testid="cal-overdue">
          <div className="cal-overdue-head">
            <Icon name="warning" width={18} height={18} />
            <strong>{plural(overdue.length, "estudio atrasado", "estudios atrasados")}</strong>
            <span className="small">ya pasó su fecha de entrega y no está validado</span>
          </div>
          <div className="cal-overdue-list">
            {(showAllOverdue ? overdue : overdue.slice(0, 4)).map((it) => (
              <DueCard key={it.order_item_id} it={it} onOpen={open} showDate showBranch={dayProps.showBranch} branchName={branchName} toneOf={toneOf} />
            ))}
          </div>
          {overdue.length > 4 && (
            <Button size="sm" variant="ghost" onClick={() => setShowAllOverdue((v) => !v)}>
              {showAllOverdue ? "Ver menos" : `Ver los ${overdue.length}`}
            </Button>
          )}
        </section>
      )}

      {view.loading && !view.data ? (
        <Loading />
      ) : view.error ? (
        <Empty icon="warning" title="No se pudo cargar el calendario" hint={errorMessage(view.error)} />
      ) : mode === "month" ? (
        <MonthGrid days={daysOf(range)} anchor={anchor} {...dayProps} onPickDay={openDay} />
      ) : mode === "week" ? (
        <div className="cal-week" data-testid="cal-week">
          {daysOf(range).map((d) => (
            <DayColumn key={d} day={d} {...dayProps} onPickDay={openDay} />
          ))}
        </div>
      ) : (
        <DayDetail day={anchor} {...dayProps} />
      )}

      {editing && (
        <EntrySheet
          key={editing.entry?.id ?? `new-${editing.date}`}
          entry={editing.entry}
          date={editing.date}
          defaultBranch={scope.branchId}
          branches={branches}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await view.reload();
          }}
        />
      )}
    </>
  );
}

interface DayProps {
  toneOf: BranchToneOf;
  labToday: string;
  dueByDay: Map<string, CalendarDueItem[]>;
  entriesByDay: Map<string, CalendarOccurrence[]>;
  open: (it: CalendarDueItem) => void;
  toggleDone: (o: CalendarOccurrence) => void;
  onEdit: (o: CalendarOccurrence) => void;
  showBranch: boolean;
  branchName: (id: string | null) => string;
}

function DueCard({ it, onOpen, showDate, showBranch, branchName, toneOf }: { it: CalendarDueItem; onOpen: (it: CalendarDueItem) => void; showDate?: boolean; showBranch?: boolean; branchName: (id: string | null) => string; toneOf?: BranchToneOf }) {
  const chip = dueChip(it);
  // Overdue stays red whatever the branch; every other card takes its branch's tint.
  const tone = it.overdue ? null : (toneOf?.(it.branch_id) ?? null);
  return (
    <button type="button" className={`cal-due ${it.overdue ? "overdue" : it.status} ${tone ? "branch-tinted" : ""}`} style={toneStyle(tone)} onClick={() => onOpen(it)} data-testid="cal-due" data-branch={tone ? (it.branch_id ?? undefined) : undefined}>
      <span className="cal-due-top">
        <span className="cal-due-patient">{it.patient_name}</span>
        <Chip tone={chip.tone}>{chip.label}</Chip>
      </span>
      <span className="cal-due-study">
        <span className="mono">{it.study_clave}</span> {it.study_name}
      </span>
      <span className="cal-due-meta small">
        <span className="mono">{it.folio}</span>
        {showDate ? ` · ${fmtDueDay(it.due_date, true)}` : ""}
        {showBranch ? ` · ${branchName(it.branch_id)}` : ""}
        {it.sample_pending ? <span className="cal-provisional"> · muestra pendiente</span> : null}
      </span>
    </button>
  );
}

function EntryRow({ o, toggleDone, onEdit, showBranch, branchName, compact, toneOf }: { o: CalendarOccurrence; toggleDone: (o: CalendarOccurrence) => void; onEdit: (o: CalendarOccurrence) => void; showBranch: boolean; branchName: (id: string | null) => string; compact?: boolean; toneOf?: BranchToneOf }) {
  const e = o.entry;
  const tone = toneOf?.(e.branch_id) ?? null;
  return (
    <div className={`cal-entry ${o.done ? "done" : ""} ${tone ? "branch-tinted" : ""}`} style={toneStyle(tone)} data-testid="cal-entry" data-branch={tone ? (e.branch_id ?? undefined) : undefined}>
      <label className="cal-entry-check" aria-label={o.done ? `Marcar «${e.title}» como pendiente` : `Marcar «${e.title}» como hecho`}>
        <input type="checkbox" checked={o.done} onChange={() => toggleDone(o)} />
      </label>
      <button type="button" className="cal-entry-body" onClick={() => onEdit(o)}>
        <span className="cal-entry-title">
          {e.entry_time ? <span className="mono cal-entry-time">{e.entry_time}</span> : null}
          {e.title}
          {e.recurrence !== "none" ? (
            <span className="cal-entry-repeat" title={recurrenceText(e) ?? undefined} aria-label={recurrenceText(e) ?? undefined}>
              <Icon name="refresh" width={13} height={13} />
            </span>
          ) : null}
        </span>
        {!compact && (e.patient_name || e.note || (showBranch && e.branch_id !== null)) && (
          <span className="small muted cal-entry-meta">
            {[e.patient_name, e.order_folio, showBranch ? branchName(e.branch_id) : null, e.note].filter(Boolean).join(" · ")}
          </span>
        )}
      </button>
    </div>
  );
}

function DayColumn({ day, toneOf, labToday, dueByDay, entriesByDay, open, toggleDone, onEdit, showBranch, branchName, onPickDay }: DayProps & { day: string; onPickDay: (d: string) => void }) {
  const due = dueByDay.get(day) ?? [];
  const entries = entriesByDay.get(day) ?? [];
  const isToday = day === labToday;
  return (
    <section className={`cal-day ${isToday ? "today" : ""} ${day < labToday ? "past" : ""}`} aria-label={fmtDueDay(day)} data-testid={`cal-day-${day}`}>
      <button type="button" className="cal-day-head" onClick={() => onPickDay(day)}>
        <span className="cal-wd">{weekdayShort(day)}</span>
        <span className="cal-dn">{Number(day.slice(8))}</span>
        {isToday && <span className="cal-today-tag">Hoy</span>}
        {due.length > 0 && <span className="cal-count">{plural(due.length, "entrega", "entregas")}</span>}
      </button>
      <div className="cal-day-body">
        {entries.map((o) => (
          <EntryRow key={`${o.entry.id}-${o.date}`} o={o} toggleDone={toggleDone} onEdit={onEdit} showBranch={showBranch} branchName={branchName} toneOf={toneOf} compact />
        ))}
        {due.map((it) => (
          <DueCard key={it.order_item_id} it={it} onOpen={open} showBranch={showBranch} branchName={branchName} toneOf={toneOf} />
        ))}
        {!due.length && !entries.length && <span className="cal-empty small faint">Sin entregas</span>}
      </div>
    </section>
  );
}

function DayDetail({ day, toneOf, labToday, dueByDay, entriesByDay, open, toggleDone, onEdit, showBranch, branchName }: DayProps & { day: string }) {
  const due = dueByDay.get(day) ?? [];
  const entries = entriesByDay.get(day) ?? [];
  const groups: { title: string; items: CalendarDueItem[] }[] = [
    { title: "Por entregar", items: due.filter((d) => d.status === "pending") },
    { title: "Listos para entregar", items: due.filter((d) => d.status === "ready") },
    { title: "Entregados", items: due.filter((d) => d.status === "delivered") },
  ].filter((g) => g.items.length);
  return (
    <div className={`cal-detail ${day === labToday ? "today" : ""}`} data-testid="cal-detail">
      <section className="card">
        <div className="card-head">
          <h3 className="h3">Entregas</h3>
          <span className="small muted">{plural(due.length, "estudio", "estudios")}</span>
        </div>
        <div className="card-body stack" style={{ gap: 14 }}>
          {groups.length === 0 && <p className="muted" style={{ margin: 0 }}>Nada prometido para este día.</p>}
          {groups.map((g) => (
            <div key={g.title} className="stack" style={{ gap: 8 }}>
              <div className="kicker">
                {g.title} ({g.items.length})
              </div>
              <div className="cal-detail-grid">
                {g.items.map((it) => (
                  <DueCard key={it.order_item_id} it={it} onOpen={open} showBranch={showBranch} branchName={branchName} toneOf={toneOf} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <section className="card">
        <div className="card-head">
          <h3 className="h3">Notas</h3>
        </div>
        <div className="card-body stack" style={{ gap: 8 }}>
          {entries.length === 0 && <p className="muted" style={{ margin: 0 }}>Sin notas para este día.</p>}
          {entries.map((o) => (
            <EntryRow key={`${o.entry.id}-${o.date}`} o={o} toggleDone={toggleDone} onEdit={onEdit} showBranch={showBranch} branchName={branchName} toneOf={toneOf} />
          ))}
        </div>
      </section>
    </div>
  );
}

/** The month grid, shared by Calendario and Inicio (Fer, 2026-11-03 10:02: "el calendario completo del mes"). */
export function MonthGrid({ days, anchor, labToday, dueByDay, entriesByDay, onPickDay, selected, toneOf }: Pick<DayProps, "labToday" | "dueByDay" | "entriesByDay"> & { toneOf?: BranchToneOf; days: string[]; anchor: string; onPickDay: (d: string) => void; selected?: string }) {
  const month = anchor.slice(0, 7);
  return (
    <div className="cal-month" data-testid="cal-month">
      {days.slice(0, 7).map((d) => (
        <div key={`h-${d}`} className="cal-month-wd">
          {weekdayShort(d)}
        </div>
      ))}
      {days.map((d) => {
        const s = daySummary(dueByDay.get(d) ?? []);
        const entries = entriesByDay.get(d) ?? [];
        const outside = d.slice(0, 7) !== month;
        return (
          <button
            type="button"
            key={d}
            className={`cal-cell ${d === labToday ? "today" : ""} ${outside ? "outside" : ""} ${d === selected ? "selected" : ""}`}
            aria-pressed={selected !== undefined ? d === selected : undefined}
            onClick={() => onPickDay(d)}
            aria-label={`${fmtDueDay(d)}: ${plural(s.total, "entrega", "entregas")}, ${plural(entries.length, "nota", "notas")}`}
            data-testid={`cal-cell-${d}`}
          >
            <span className="cal-cell-dn">{Number(d.slice(8))}</span>
            {s.total > 0 && !toneOf && (
              <span className={`cal-cell-due ${s.overdue ? "overdue" : s.pending ? "pending" : "done"}`}>
                {plural(s.total, "entrega", "entregas")}
              </span>
            )}
            {s.total > 0 && toneOf && <BranchDuePills due={dueByDay.get(d) ?? []} toneOf={toneOf} />}
            {entries.slice(0, 3).map((o) => {
              const tone = toneOf?.(o.entry.branch_id) ?? null;
              return (
                <span key={`${o.entry.id}-${o.date}`} className={`cal-cell-entry ${o.done ? "done" : ""} ${tone ? "branch-tinted" : ""}`} style={toneStyle(tone)} data-branch={tone ? (o.entry.branch_id ?? undefined) : undefined}>
                  {o.entry.title}
                </span>
              );
            })}
            {entries.length > 3 && <span className="cal-cell-more small">+{entries.length - 3} más</span>}
          </button>
        );
      })}
    </div>
  );
}

/** Add or edit one note. Kept short for a tablet: title and date first, everything else optional. */
function EntrySheet({ entry, date, defaultBranch, branches, onClose, onSaved }: { entry: CalendarEntry | null; date: string; defaultBranch: string | null; branches: { id: string; name: string }[]; onClose: () => void; onSaved: () => void | Promise<void> }) {
  const toast = useToast();
  const branch = useBranch();
  const [title, setTitle] = useState(entry?.title ?? "");
  const [day, setDay] = useState(entry?.entry_date ?? date);
  const [time, setTime] = useState(entry?.entry_time ?? "");
  const [branchId, setBranchId] = useState<string>(entry ? (entry.branch_id ?? ALL) : (defaultBranch ?? ALL));
  const [repeat, setRepeat] = useState<RecurrenceValue>({ recurrence: entry?.recurrence ?? "none", interval: entry?.recurrence_interval ?? 1, until: entry?.recurrence_until ?? null });
  const [note, setNote] = useState(entry?.note ?? "");
  const [patient, setPatient] = useState<{ id: string; name: string } | null>(entry?.patient_id ? { id: entry.patient_id, name: entry.patient_name ?? "" } : null);
  const [orderId, setOrderId] = useState<string | null>(entry?.order_id ?? null);
  const [pickingPatient, setPickingPatient] = useState(false);
  const [busy, setBusy] = useState(false);
  const orders = useQuery(() => get("orders", { query: { patient_id: patient!.id, limit: 8 } }), [patient?.id], { enabled: !!patient });
  const titleOk = title.trim().length > 0;
  const repeatOk = repeat.recurrence === "none" || repeat.until === null || repeat.until >= day;

  async function save() {
    setBusy(true);
    try {
      await run(
        "calendar.entry.upsert",
        {
          entry_id: entry?.id ?? null,
          title: title.trim(),
          entry_date: day,
          entry_time: time || null,
          branch_id: branchId === ALL ? null : branchId,
          note: note.trim() || null,
          recurrence: repeat.recurrence,
          recurrence_interval: repeat.recurrence === "none" ? 1 : repeat.interval,
          recurrence_until: repeat.recurrence === "none" ? null : repeat.until,
          patient_id: patient?.id ?? null,
          order_id: orderId,
        },
        { branch_id: branch.id },
      );
      toast.success(entry ? "Nota actualizada." : "Nota agregada al calendario.");
      await onSaved();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!entry) return;
    setBusy(true);
    try {
      await run("calendar.entry.remove", { entry_id: entry.id }, { branch_id: branch.id });
      toast.success("Nota eliminada.");
      await onSaved();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title={entry ? "Editar nota" : "Agregar nota"}
      footer={
        <div className="row" style={{ width: "100%" }}>
          {entry && (
            <Button variant="ghost" icon="trash" busy={busy} onClick={() => void remove()}>
              Eliminar
            </Button>
          )}
          <span style={{ flex: 1 }} />
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!titleOk || !day || !repeatOk} onClick={() => void save()}>
            Guardar
          </Button>
        </div>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        <Field label="¿Qué hay que hacer?">
          <Input aria-label="¿Qué hay que hacer?" {...NO_AUTOFILL} autoFocus={!entry} maxLength={120} placeholder="Depositar, RPBI, pagar internet, cita para muestra…" value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <div className="grid-2">
          <Field label="Fecha">
            <Input aria-label="Fecha" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          </Field>
          <Field label="Hora" optional>
            <Input aria-label="Hora" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </Field>
        </div>
        <div className="grid-2">
          <Field label="Sucursal">
            <Select aria-label="Sucursal de la nota" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value={ALL}>Todas las sucursales</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <RecurrenceField value={repeat} onChange={setRepeat} firstDay={day} />
        <div className="field" role="group" aria-label="Paciente">
          <span className="label">Paciente <span className="opt">(opcional)</span></span>
          {patient ? (
            <div className="row" style={{ gap: 8 }}>
              <span className="cal-patient">
                <Icon name="user" width={16} height={16} /> {patient.name}
              </span>
              <Button size="sm" variant="ghost" onClick={() => { setPatient(null); setOrderId(null); }}>
                Quitar
              </Button>
            </div>
          ) : pickingPatient ? (
            <PatientSearch
              autoFocus
              onPick={(p: PatientSummary) => {
                setPatient({ id: p.id, name: [p.first_name, p.paternal_surname, p.maternal_surname].filter(Boolean).join(" ") });
                setPickingPatient(false);
              }}
            />
          ) : (
            <Button variant="secondary" icon="search" onClick={() => setPickingPatient(true)}>
              Buscar paciente
            </Button>
          )}
          <span className="hint">Para una cita de toma de muestra.</span>
        </div>
        {patient && (orders.data?.items.length ?? 0) > 0 && (
          <Field label="Solicitud" optional>
            <Select aria-label="Solicitud de la cita" value={orderId ?? ""} onChange={(e) => setOrderId(e.target.value || null)}>
              <option value="">Sin solicitud</option>
              {orders.data!.items.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.folio}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Notas" optional>
          <Textarea aria-label="Notas" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {entry?.order_id && (
          <Link to={`/solicitudes/${entry.order_id}`} className="small">
            Ver la solicitud {entry.order_folio}
          </Link>
        )}
      </div>
    </Sheet>
  );
}

/**
 * A month cell's deliveries with a branch lookup: one pill per branch in that branch's tint when
 * several branches are on screen (overdue ones stay red); with a single branch the usual
 * status pill, so the lookup being present never changes a one-branch view.
 */
function BranchDuePills({ due, toneOf }: { due: CalendarDueItem[]; toneOf: BranchToneOf }) {
  const groups = new Map<string, CalendarDueItem[]>();
  for (const it of due) groups.set(it.branch_id ?? "", [...(groups.get(it.branch_id ?? "") ?? []), it]);
  const tinted = [...groups.keys()].some((id) => id !== "" && toneOf(id) !== null);
  if (!tinted) {
    const s = daySummary(due);
    return <span className={`cal-cell-due ${s.overdue ? "overdue" : s.pending ? "pending" : "done"}`}>{plural(s.total, "entrega", "entregas")}</span>;
  }
  return (
    <>
      {[...groups.entries()].map(([branchId, items]) => {
        const overdue = items.some((i) => i.overdue);
        const tone = overdue ? null : toneOf(branchId || null);
        return (
          <span key={branchId} className={`cal-cell-due ${overdue ? "overdue" : tone ? "branch-tinted" : "pending"}`} style={toneStyle(tone)} data-branch={tone ? branchId : undefined}>
            {plural(items.length, "entrega", "entregas")}
          </span>
        );
      })}
    </>
  );
}

/** «● Matriz Villa El Capulín ● Sucursal San José Iturbide»: what each tint means while both branches show. */
export function BranchLegend({ branches, toneOf }: { branches: readonly { id: string; name: string }[]; toneOf: BranchToneOf }) {
  return (
    <ul className="branch-legend" aria-label="Colores por sucursal" data-testid="branch-legend">
      {[...branches]
        .sort((a, b) => a.id.localeCompare(b.id))
        .map((b) => (
          <li key={b.id}>
            <span className="branch-legend-dot" style={{ background: toneOf(b.id)?.edge }} aria-hidden="true" />
            {b.name}
          </li>
        ))}
    </ul>
  );
}
